import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {CATEGORY_ARRAY, HOME, SITEMAP} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {getSearchBarVariant} from '@helpers';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {tagService} from '@jobseeker/common-core/services';
import type {GetServerSidePropsContext} from 'next/types';
import {Box, Grid, Typography} from '@mui/material';
import {Link} from '@jobseeker/lotus/elements';

type Category = {dataId: string; label: string; tag?: string};
type SitemapProps = {
  seoConfig: object;
  metaData: {header: {meta: {tabsList: Category[]}}};
  categoryId: number;
  urls: {tagUrl: string; tagName: string; tagAndLocationUrl: string}[];
  canonicalUrl: string;
};

const Category = ({
  seoConfig,
  metaData,
  categoryId,
  urls,
  canonicalUrl,
}: SitemapProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const {
    header: {
      meta: {tabsList},
    },
  } = metaData || {};

  console.log(tabsList);

  const {label: categoryName} = isAppIimjobs
    ? tabsList.find((cat: Category) => cat.dataId === `${categoryId}`) ?? {
        dataId: '0',
        label: '',
        tag: '',
      }
    : CATEGORY_ARRAY.find(
        (cat: Category) => cat.dataId === `${categoryId}`
      ) ?? {
        catId: '0',
        label: '',
        tag: '',
      };

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Sitemap',
      item: `/sitemap`,
    },
    {
      name: `Jobs Title - ${categoryName}`,
      item: '',
    },
  ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout
        seoConfig={{
          ...seoConfig,
          canonical: `${
            (seoConfig as {canonical?: string}).canonical
          }${canonicalUrl.replace('/sitemap', '')}`,
        }}
      />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          <Box
            sx={{
              mx: isAppIimjobs ? 0 : 2,
            }}
          >
            <Typography component="h2" mb={2} mt={isAppIimjobs ? 0 : 6}>
              {categoryName} Jobs
            </Typography>

            <Grid container>
              {urls.map((url, index) => (
                <Grid item key={index} xs={4}>
                  <Link href={url.tagUrl} target="_blank">
                    {url.tagName} Jobs
                  </Link>
                  {` > `}
                  <Link href={url.tagAndLocationUrl} target="_blank">
                    Location
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const searchBarVariant = await getSearchBarVariant({req: context.req});
    store.dispatch({
      type: 'AppContext/set_ab_variants',
      payload: {searchBar: searchBarVariant},
    });
    const canonicalUrl = context?.resolvedUrl;
    const {slug = ''} = context.query;
    let categoryId = 0;

    try {
      categoryId = parseInt(
        String(slug)
          .replace(/\.html/, '')
          .split('-')
          .pop() ?? '0',
        10
      );
    } catch (err) {}

    const metaData = getConfigByApp(metaInfo, HOME, true, SITEMAP);

    const seoConfig = await loadSEO(SITEMAP);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SITEMAP},
    });

    const keywords: {id: number; tag_name: string; tag_name_lower: string}[] =
      (await tagService.getTags('', {categoryId, char: ''})) ?? [];

    const urls: {tagUrl: string; tagName: string; tagAndLocationUrl: string}[] =
      [];

    const baseUrl = process.env.NEXT_PUBLIC_LOAD_CONFIG;

    keywords?.forEach(({id, tag_name, tag_name_lower}) => {
      const tagUrl = new URL(`/k/${tag_name_lower}-jobs`, baseUrl).href;

      const tagAndLocationUrl = new URL(`/sitemap/kl/${id}`, baseUrl).href;

      urls.push({
        tagUrl,
        tagName: tag_name,
        tagAndLocationUrl,
      });
    });

    urls.sort((urlA, urlB) => {
      if (urlA.tagName.toUpperCase() < urlB.tagName.toUpperCase()) {
        return -1;
      }
      return 1;
    });
    return {
      props: {
        seoConfig,
        metaData,
        categoryId,
        urls,
        canonicalUrl,
      },
    };
  }
);

export default Category;
